//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useState } from 'react';

import DateRangePickerPanel from '@components/date-range-picker/DateRangePickerPanel';
import {
  getReadableDateRangeValue,
  IAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import ButtonPopover from '@components/molecules/button-popover/ButtonPopover';
import { usePopover } from '@components/molecules/popover/usePopover';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { ITrackableComponent } from '@utils/withTracking';

type IPopoverProps = Pick<
  React.ComponentProps<typeof ButtonPopover>,
  'appendTarget' | 'placement'
>;

interface IOwnProps extends ITrackableComponent, IPopoverProps {
  aliases?: IAliasedDateRange[];
  className?: string;
  footnote?: React.ReactNode;
  isDisabled?: boolean;
  maxDate?: DateString;
  maxDateRange?: number;
  minDate?: DateString;
  onDateRangeChanged: (dateRange: IAliasedDateRange) => void;
  timeZone: TimeZoneId;
  value: IAliasedDateRange;
}

const DateRangePicker = ({
  appendTarget,
  className,
  isDisabled,
  onDateRangeChanged,
  placement = 'bottom-start',
  trackingId,
  value,
  ...pickerProps
}: IOwnProps): JSX.Element => {
  const [connectPopover, { closePopover }] = usePopover();
  // Using thia because popoverMonitor isn't being updated with
  // with visibility state when toggling popover
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ButtonPopover
      className={className}
      connectPopover={connectPopover}
      isDisabled={isDisabled}
      trackingId={trackingId}
      placeholderText='Select a date range'
      valueText={getReadableDateRangeValue(value)}
      placement={placement}
      appendTarget={appendTarget}
      content={
        <DateRangePickerPanel
          initialValue={value}
          isOpen={isOpen}
          onSetDateClicked={(alias) => {
            onDateRangeChanged(alias);
            closePopover();
            setIsOpen(false);
          }}
          onCancelClicked={() => {
            closePopover();
            setIsOpen(false);
          }}
          {...pickerProps}
        />
      }
      onTogglePopover={(isOpen, _) => {
        setIsOpen(isOpen);
      }}
    ></ButtonPopover>
  );
};

export default DateRangePicker;
